"use client";

import Link from "next/link";
import { Eye, EyeOff, CheckCircle2, Mail, Loader2 } from "lucide-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  authService,
  DEFAULT_PLAN_POST_LOGIN_PATH,
  userHasDefaultSubscriptionPlan,
} from "@/services/auth-service";
import { useAppDispatch } from "@/redux/hooks";
import { AppDispatch, store } from "@/redux/store";
import { establishSessionWithTokens } from "@/redux/features/auth-thunk";
import { cn } from "@/lib/utils";
import { useLoginCooldown } from "@/hooks/use-login-cooldown";
import {
  getRetryAfterSeconds,
  isCredentialFailure,
  validateLoginEmail,
  validateLoginPassword,
} from "@/utils/login";

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

export default function OrganizationLogin() {
  const dispatch = useAppDispatch() as AppDispatch;
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const {
    isCoolingDown,
    remainingSeconds,
    recordFailedAttempt,
    resetFailedAttempts,
  } = useLoginCooldown();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setErrors((prev) => ({ ...prev, general: undefined }));
    if (touched.email) {
      setErrors((prev) => ({ ...prev, email: validateLoginEmail(value) }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setErrors((prev) => ({ ...prev, general: undefined }));
    if (touched.password) {
      setErrors((prev) => ({ ...prev, password: validateLoginPassword(value) }));
    }
  };

  const handleBlur = (field: "email" | "password") => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === "email") {
      setErrors((prev) => ({ ...prev, email: validateLoginEmail(email) }));
    } else {
      setErrors((prev) => ({
        ...prev,
        password: validateLoginPassword(password),
      }));
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCoolingDown) {
      return;
    }

    const emailError = validateLoginEmail(email);
    const passwordError = validateLoginPassword(password);

    const newErrors = {
      email: emailError,
      password: passwordError,
    };

    setErrors(newErrors);
    setTouched({ email: true, password: true });

    if (emailError || passwordError) {
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});
      const { data } = await authService.login({
        email: email.trim(),
        password,
      });
      resetFailedAttempts();

      const accessToken = data?.access_token;
      const refreshToken = data?.refresh_token;

      if (
        typeof accessToken === "string" &&
        accessToken.length > 0 &&
        typeof refreshToken === "string" &&
        refreshToken.length > 0
      ) {
        await dispatch(
          establishSessionWithTokens({
            access_token: accessToken,
            refresh_token: refreshToken,
          })
        ).unwrap();
        const user = store.getState().user.user;
        const path = userHasDefaultSubscriptionPlan(user)
          ? DEFAULT_PLAN_POST_LOGIN_PATH
          : "/teams";
        router.replace(path);
        return;
      }

      const message = data?.message;
      setSuccessNotice(
        typeof message === "string" && message.length > 0
          ? message
          : "Check your email for a link to complete sign in."
      );
      setSubmitted(true);
    } catch (error: unknown) {
      const retryAfterSeconds = getRetryAfterSeconds(error);
      if (retryAfterSeconds !== undefined) {
        recordFailedAttempt(retryAfterSeconds);
        setErrors({
          general: "Too many login attempts. Please wait before trying again.",
        });
      } else if (isCredentialFailure(error)) {
        recordFailedAttempt();
        setErrors({ general: "Invalid email or password." });
      } else {
        setErrors({
          general: "Unable to sign in right now. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="relative w-full max-w-[440px]">
        <CardHeader className="mb-8 space-y-0 p-0 text-center">
          <div className="mx-auto mb-2 flex size-14 items-center justify-center rounded-xl bg-violet-50 text-primary">
            <Mail className="size-7" aria-hidden />
          </div>
          <h2 className="mb-2 text-[32px] font-extrabold leading-tight tracking-tight text-foreground">
            Check your email
          </h2>
        </CardHeader>

        <div className="flex gap-3 rounded-xl border border-authBorder bg-violet-50 p-4 text-sm leading-relaxed text-muted-foreground">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
          <p>{successNotice}</p>
        </div>

        <div className="mt-2 flex flex-col gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-full border-authBorder text-sm font-semibold text-muted-foreground hover:border-violet-200 hover:bg-violet-50 hover:text-foreground"
            onClick={() => {
              setSubmitted(false);
              setSuccessNotice(null);
              setErrors({});
            }}
          >
            Use a different account
          </Button>
        </div>

        <div className="mt-4 flex justify-center">
          <Button
            type="button"
            variant="link"
            className="px-0 text-sm font-semibold text-primary underline-offset-2"
            onClick={() => {
              setSubmitted(false);
              setSuccessNotice(null);
              setErrors({});
            }}
          >
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-[440px]">
      <CardHeader className="mb-8 space-y-0 p-0">
        <h1 className="mb-2 text-[32px] font-extrabold leading-tight tracking-tight text-foreground">
          Organisation Login
        </h1>
        <p className="max-w-[420px] text-[15px] text-muted-foreground">
          Enter information to login to your account
        </p>
      </CardHeader>

      <form method="post" onSubmit={handleLogin}>
        <div className="mb-[18px] space-y-1.5">
          <Label htmlFor="email" className="text-[13px] font-semibold text-foreground">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={() => handleBlur("email")}
            className={cn(
              "h-12 rounded-xl border-authBorder bg-white px-3.5 text-base text-neutral-900 shadow-none placeholder:text-muted-foreground hover:border-violet-200 focus-visible:border-primary focus-visible:ring-primary/20",
              errors.email && "border-destructive focus-visible:ring-destructive/20"
            )}
          />
          {touched.email && errors.email && (
            <p className="text-[13px] leading-snug text-destructive">{errors.email}</p>
          )}
        </div>

        <div className="mb-[18px] space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <Label htmlFor="password" className="text-[13px] font-semibold text-foreground">
              Password
            </Label>
            <Link
              href="/forgot-password"
              className="shrink-0 text-[13px] font-semibold text-primary hover:text-primary/80 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••••"
              autoComplete="current-password"
              value={password}
              onChange={handlePasswordChange}
              onBlur={() => handleBlur("password")}
              className={cn(
                "h-12 rounded-xl border-authBorder bg-white px-3.5 pr-12 text-base text-neutral-900 shadow-none placeholder:text-muted-foreground hover:border-violet-200 focus-visible:border-primary focus-visible:ring-primary/20",
                errors.password && "border-destructive focus-visible:ring-destructive/20"
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 size-9 -translate-y-1/2 rounded-lg text-muted-foreground hover:bg-violet-50 hover:text-primary"
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="size-[18px]" />
              ) : (
                <Eye className="size-[18px]" />
              )}
            </Button>
          </div>
          {touched.password && errors.password && (
            <p className="text-[13px] leading-snug text-destructive">{errors.password}</p>
          )}
        </div>

        {errors.general && (
          <p
            className="mb-[18px] rounded-xl bg-destructive/10 p-2.5 text-[13px] leading-snug text-destructive"
            role="alert"
          >
            {errors.general}
          </p>
        )}

        {isCoolingDown && (
          <p
            className="mb-[18px] rounded-xl bg-muted p-2.5 text-[13px] leading-snug text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            Login is temporarily paused. Try again in {remainingSeconds} seconds.
          </p>
        )}

        <Button
          type="submit"
          className="mt-2 h-[52px] w-full rounded-full text-[15px] font-bold shadow-cta transition-all hover:-translate-y-px hover:bg-primaryHover hover:shadow-cta-hover"
          disabled={
            isSubmitting ||
            isCoolingDown ||
            Object.keys(errors).some(
              (key) => key !== "general" && !!errors[key as keyof FormErrors]
            )
          }
        >
          {isSubmitting ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Logging in…
            </>
          ) : isCoolingDown ? (
            `Try again in ${remainingSeconds}s`
          ) : (
            "Login"
          )}
        </Button>
      </form>
    </div>
  );
}
